import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TabSection, Chip, IconDiv, Calender, FilteredText } from './styles';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import { Dropdown, DropdownDivider, DropdownItem } from '../dropdown';
import { rootStore } from '../../mobx/rootStore';
import { dueDateItems, filterOptions, menuItems } from '../../helpers/list';
import {
  AVATAR_BORDER,
  ICON_COLORS,
  ICON_SIZE,
  PRIORITY_ICON_NAMES,
  PROJECT_ICON_TYPE,
  STATUS_TYPE_LABELS,
  statusItems
} from '../../global/constants';
import { VIEW_FILTER_LABELS, ViewFilters, VIEWS_FILTER_TYPE } from '../../mobx/interfaces/view';
import { observer } from 'mobx-react-lite';
import Avatar from '../avatar';
import { isEmpty, renderStatusIconName } from '../../helpers/common';
import AssigneeDropdownContent from '../dropdowns/assignPopup/assigneeDropdownContent';
import { UserModel } from '../../mobx/models/user';
import ProjectIconAvatar from '../projectIconAvatar';
import Calendar from 'react-calendar';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import dayjs from 'dayjs';
import Image from '../image';

interface FilterChipsProps {
  onChangePriority: (type: number) => void;
  onChangeStatus: (type: number) => void;
  onChangeProject: (id: string) => void;
  onChangeDueDate: (date: string) => void;
  onChangeAssign: (user: UserModel) => void;
  onChangeUnassign: (unassigned: string) => void;
  onChangeCustomer: (customer: CustomerDetailsInterface) => void;
}

type FilterValue = {
  [key: string]: (string | number)[];
};

const FilterChips: React.FC<FilterChipsProps> = (props: FilterChipsProps) => {
  const {
    onChangePriority,
    onChangeStatus,
    onChangeProject,
    onChangeDueDate,
    onChangeAssign,
    onChangeUnassign,
    onChangeCustomer
  } = props;
  //Mobx store variables
  const { viewStore, projectStore, workspaceStore, customerStore } = rootStore;
  const { createViewInput } = viewStore;
  const { projects } = projectStore;
  const { currentWorkspace } = workspaceStore;
  const { customers } = customerStore;
  //State variables
  const [projectList, setProjectList] = useState(projects);
  const [userList, setUserList] = useState(currentWorkspace?.users);
  const [customerList, setCustomerList] = useState(customers);

  useEffect(() => {
    // Reset customer list whenever Redux customers or MobX selected customer filter changes
    setCustomerList(customers);
  }, [customers, createViewInput.filters?.customer?.customers]);

  const dueDateOperatorOptions = [
    {
      label: 'after',
      value: VIEWS_FILTER_TYPE.AFTER
    },
    {
      label: 'before',
      value: VIEWS_FILTER_TYPE.BEFORE
    }
  ];

  const operatorOptions = (isMany: boolean, isDueDate: boolean) => {
    if (isDueDate) {
      return dueDateOperatorOptions;
    }
    return [
      {
        label: isMany ? 'is any of' : 'is',
        value: VIEWS_FILTER_TYPE.IS
      },
      {
        label: 'is not',
        value: VIEWS_FILTER_TYPE.IS_NOT
      }
    ];
  };

  const getFilterIcon = (filterName: string) => {
    return filterOptions[filterName as keyof typeof filterOptions];
  };

  const onChangeProjectSearch = useCallback(
    (value: string) => {
      let updatedList = projects;
      if (value) {
        const searchNewText = value.trim().toLowerCase();
        const words = searchNewText.split(' ').filter((word) => word.length > 0);
        updatedList = projects?.filter((item) => {
          const title = item?.name.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        });
      }
      setProjectList(updatedList);
    },
    [projects]
  );

  const onChangeAssignSearch = useCallback(
    (value: string) => {
      let updatedList = currentWorkspace?.users;
      if (value) {
        const searchNewText = value.trim().toLowerCase();
        const words = searchNewText.split(' ').filter((word) => word.length > 0);
        updatedList = currentWorkspace?.users?.filter((item) => {
          const title = item?.name.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        });
      }
      setUserList(updatedList);
    },
    [currentWorkspace?.users]
  );

  const onChangeCustomerSearch = useCallback(
    (value: string) => {
      let updatedList = customers;
      if (value) {
        const searchNewText = value.trim().toLowerCase();
        const words = searchNewText.split(' ').filter((word) => word.length > 0);
        updatedList = customers?.filter((item: CustomerDetailsInterface) => {
          const title = item?.companyName?.trim().toLowerCase() || '';
          return words.some((word) => title.includes(word));
        });
      }
      setCustomerList(updatedList);
    },
    [customers]
  );

  const getFilterDropdownContent = useCallback(
    (filterName: string, filterValue: FilterValue) => {
      const data = Object.values(filterValue)[0] as string[] | number[];

      switch (filterName) {
        case 'priority': {
          let iconName: string | undefined = 'Priority-Multiple-icon';
          if (data?.length === 1) {
            // Single priority — pick specific icon
            const selectedPriority = data[0] as number;
            iconName = menuItems.find((item) => item.key === selectedPriority)?.icon || 'Priority-Low-icon';
          }
          return {
            type: filterName,
            label: data?.length === 1 ? PRIORITY_ICON_NAMES[data[0] as number] : `${data?.length} priorities`,
            icon: iconName
          };
        }

        case 'dueDate':
          if (data.length === 2) {
            const start = dayjs(data[0], ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM/DD/YYYY']).format('DD/MM/YY');
            const end = dayjs(data[1], ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM/DD/YYYY']).format('DD/MM/YY');
            return {
              type: filterName,
              label: `${start} - ${end}`,
              icon: 'calendar-icon'
            };
          }
          return {
            type: filterName,
            label: data[0] as string //Need changes
            // icon: 'calendar-icon'
          };
        case 'status':
          return {
            type: filterName,
            label: data?.length === 1 ? STATUS_TYPE_LABELS[data[0] as number] : `${data?.length} statuses`,
            icon: 'task-todo-icon'
          };
        case 'project':
          return {
            type: filterName,
            label:
              data?.length === 1
                ? projects?.find((project) => project.id === data[0])?.name
                : `${data?.length} projects`,
            icon: 'project-icon'
          };
        case 'assign':
          return {
            type: filterName,
            label:
              data?.length === 1
                ? currentWorkspace?.users?.find((user) => user.id === data[0])?.name
                : `${data?.length} assignees`,
            icon: 'user-icon'
          };
        case 'customer':
          return {
            type: filterName,
            label:
              data?.length === 1
                ? customers?.find((customer: CustomerDetailsInterface) => customer.id === data[0])?.companyName
                : `${data?.length} customers`,
            icon: 'user-icon'
          };
        default:
          return;
      }
    },
    [currentWorkspace?.users, projects, customers]
  );

  const onChangeDueRange = useCallback(
    (v: Date | [Date | null, Date | null]) => {
      const range: [Date | null, Date | null] = Array.isArray(v) ? v : [v, v];
      if (range[0] && range[1]) {
        // Convert to local YYYY-MM-DD format
        const start = dayjs(range[0]).startOf('day').format('YYYY-MM-DD');
        const end = dayjs(range[1]).endOf('day').format('YYYY-MM-DD');
        createViewInput.setDueDateFilter([start, end], VIEWS_FILTER_TYPE.RANGE);
      }
    },
    [createViewInput]
  );

  const formattedDates: [Date | null, Date | null] = useMemo(() => {
    return [
      createViewInput?.filters.dueDate.startDate ? new Date(createViewInput?.filters.dueDate.startDate) : null,
      createViewInput?.filters.dueDate.endDate ? new Date(createViewInput?.filters.dueDate.endDate) : null
    ];
  }, [createViewInput?.filters.dueDate.startDate, createViewInput?.filters.dueDate.endDate]);

  const getFilterDropdown = useCallback(
    (filterName: string) => {
      switch (filterName) {
        case 'priority':
          return menuItems.map((item, index) => {
            const isChecked = createViewInput?.filters?.priority?.priorities.includes(item.key);
            return (
              <DropdownItem
                key={index}
                label={item.label}
                onClick={() => onChangePriority(item.key)}
                iconName={item.icon}
                isCheckBox={true}
                isChecked={isChecked}
              />
            );
          });
        case 'status':
          return statusItems.map((item, index) => {
            const isChecked = createViewInput?.filters?.status?.statuses.includes(item.type);
            return (
              <DropdownItem
                key={index}
                label={item.label}
                onClick={() => onChangeStatus(item.type)}
                iconName={item.iconName}
                isCheckBox={true}
                isChecked={isChecked}
              />
            );
          });
        case 'project':
          return (
            <>
              {projectList?.map((item, index) => {
                const isChecked = createViewInput?.filters?.project?.projects.includes(item.id);
                return (
                  <DropdownItem
                    key={index}
                    label={item.name}
                    onClick={() => onChangeProject(item.id)}
                    isCheckBox={true}
                    isChecked={isChecked}
                  />
                );
              })}
            </>
          );
        case 'dueDate':
          return (
            <>
              {dueDateItems.map((item, index) => {
                return (
                  <DropdownItem
                    key={index}
                    label={item.label}
                    onClick={() => {
                      onChangeDueDate(item.label);
                    }}
                  />
                );
              })}
              <DropdownDivider />
              <Calender role='button' onClick={(e) => e.stopPropagation()}>
                <Calendar
                  selectRange={true}
                  value={formattedDates}
                  onChange={onChangeDueRange}
                  prev2Label={
                    <SVGIcon
                      name='calender-prev2Label'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                    />
                  }
                  prevLabel={
                    <SVGIcon
                      name='calender-prevLabel'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                    />
                  }
                  next2Label={
                    <SVGIcon
                      name='calender-next2Label'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                    />
                  }
                  nextLabel={
                    <SVGIcon
                      name='calender-nextLabel'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                    />
                  }
                />
              </Calender>
            </>
          );
        case 'assign': {
          const filterAssignees = createViewInput?.filters?.assign?.assignees || [];
          return (
            <AssigneeDropdownContent
              data={userList || []}
              isMultiAssignee={true}
              showUnassignedCheckbox={true}
              isUnassignedChecked={filterAssignees.includes('unassigned')}
              isUserAssigned={(userId) => filterAssignees.includes(userId)}
              onClickAssignUser={onChangeAssign}
              onClickUnassignUser={() => onChangeUnassign('unassigned')}
            />
          );
        }
        case 'customer':
          if (isEmpty(customerList)) return null;
          return (
            <>
              {customerList?.map((item: CustomerDetailsInterface, index: number) => {
                const isChecked = createViewInput?.filters?.customer?.customers.includes(item.id);
                return (
                  <DropdownItem
                    key={index}
                    label={item.companyName}
                    onClick={() => onChangeCustomer(item)}
                    isCheckBox={true}
                    isChecked={isChecked}
                    isAvatar={true}
                    avatarSrc={item.profileImage ? item.profileImage : 'empty_profile'}
                  />
                );
              })}
            </>
          );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createViewInput?.filters?.assign?.assignees,
      createViewInput?.filters?.priority?.priorities,
      createViewInput?.filters?.project?.projects,
      createViewInput?.filters?.customer?.customers,
      createViewInput?.filters?.status?.statuses,
      createViewInput?.filters?.dueDate?.date,
      currentWorkspace?.users,
      userList,
      customerList,
      onChangeDueDate,
      onChangePriority,
      onChangeProject,
      onChangeCustomer,
      onChangeStatus,
      projectList,
      projects,
      customers
    ]
  );

  const tabSectionContent = useMemo(() => {
    return Object.entries(createViewInput?.filters || {}).map(([filterName, filterValue]) => {
      const filterIcon = getFilterIcon(filterName);
      const filterDropdownContent = getFilterDropdownContent(filterName, filterValue);
      const filterDropdown = getFilterDropdown(filterName);
      const filterType = filterValue.type;
      return {
        filterName,
        filterIcon,
        filterValue,
        filterDropdownContent,
        filterDropdown,
        filterType
      };
    });
  }, [createViewInput?.filters, getFilterDropdown, getFilterDropdownContent]);

  const selectedFilter = useCallback(
    (filterName: string, value: number[] | string[] | number, type: number) => {
      switch (filterName) {
        case 'status':
          return createViewInput.setStatusFilter(value as number[], type);
        case 'priority':
          return createViewInput.setPriorityFilter(value as number[], type);
        case 'assign':
          return createViewInput.setAssignFilter(value as string[], type);
        case 'project':
          return createViewInput.setProjectFilter(value as string[], type);
        case 'customer':
          return createViewInput.setCustomerFilter(value as string[], type);
        case 'dueDate':
          return createViewInput.setDueDateFilter(value as string[], type);
      }
    },
    [createViewInput]
  );

  const onChangeOperator = useCallback(
    (filterName: string, operator: number) => {
      const filterValue = createViewInput?.filters?.[filterName as keyof ViewFilters];
      const key = Object.keys(filterValue)[0];
      const value = filterValue[key as keyof typeof filterValue];
      selectedFilter(filterName, value, operator);
    },
    [createViewInput?.filters, selectedFilter]
  );

  const onRemoveFilter = useCallback(
    (filterName: string) => {
      selectedFilter(filterName, [], filterName === 'dueDate' ? VIEWS_FILTER_TYPE.AFTER : VIEWS_FILTER_TYPE.IS);
    },
    [selectedFilter]
  );

  return (
    <>
      {tabSectionContent?.map((item) => {
        const { filterName, filterIcon, filterValue, filterDropdownContent, filterDropdown } = item;
        const data = Object.values(filterValue)[0] as string[] | number[];

        if (data?.length === 0) return null;
        return (
          <TabSection>
            <Chip className='first-child'>
              <IconDiv>
                <SVGIcon
                  name={filterIcon.iconName}
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={filterIcon.viewBox}
                  fill='var(--text-primary)'
                />
              </IconDiv>
              <span>{filterIcon.label}</span>
            </Chip>
            {filterName === 'dueDate' && filterValue?.type === VIEWS_FILTER_TYPE.RANGE && (data?.length || 0) > 1 ? (
              <Chip>
                <span>{VIEW_FILTER_LABELS[VIEWS_FILTER_TYPE.RANGE]}</span>
              </Chip>
            ) : (
              <Dropdown
                activeClassName='active'
                content={
                  <Chip>
                    <span>
                      {data?.length > 1 && filterValue.type === VIEWS_FILTER_TYPE.IS
                        ? 'is any of'
                        : VIEW_FILTER_LABELS[filterValue.type]}
                    </span>
                  </Chip>
                }
                trigger='click'>
                {operatorOptions(
                  data?.length > 1,
                  filterName === 'dueDate' &&
                    filterDropdownContent?.label !== 'In the past' &&
                    filterDropdownContent?.label !== 'No due date'
                ).map((option) => (
                  <DropdownItem
                    key={option.value}
                    label={option.label}
                    onClick={() => onChangeOperator(filterName, option.value)}
                  />
                ))}
              </Dropdown>
            )}
            <Dropdown
              isMinWidth={filterName === 'dueDate' ? 250 : 138}
              isRemoveMaxHeight={filterName === 'dueDate'}
              modalTitle={
                filterName === 'dueDate' ? 'Due date' : filterName.charAt(0).toUpperCase() + filterName.slice(1)
              }
              activeClassName='active'
              onChangeSearch={
                filterName === 'customer'
                  ? onChangeCustomerSearch
                  : filterName === 'project'
                  ? onChangeProjectSearch
                  : filterName === 'assign'
                  ? onChangeAssignSearch
                  : undefined
              }
              content={
                <Chip>
                  {data?.length === 1 &&
                    filterName !== 'status' &&
                    filterName !== 'assign' &&
                    filterName !== 'project' &&
                    filterName !== 'customer' &&
                    filterDropdownContent?.icon && (
                      <IconDiv key={filterDropdownContent?.label}>
                        <SVGIcon
                          name={filterDropdownContent?.icon}
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={
                            filterName === 'dueDate'
                              ? `0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`
                              : `0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`
                          }
                          stroke={filterName === 'status' ? 'var(--text-primary)' : ''}
                        />
                      </IconDiv>
                    )}

                  {(filterName === 'assign' ||
                    filterName === 'status' ||
                    filterName === 'project' ||
                    filterName === 'customer') &&
                    data?.map((item, index) => {
                      const user =
                        filterName === 'assign' ? currentWorkspace?.users?.find((user) => user.id === item) : null;
                      const customer =
                        filterName === 'customer' ? customerList?.find((customer) => customer.id === item) : null;
                      let projectDetails;
                      if (filterName === 'project') {
                        projectDetails = projects?.find((project) => project.id === item);
                      }
                      if (index > 1) return null;
                      return (
                        <IconDiv key={index} marginLeft={index > 0 ? '-12px' : 'var(--space-0)'}>
                          {filterName === 'assign' ? (
                            <Avatar
                              imgSrc={user?.profile_photo || ''}
                              name={user?.name || (item === 'unassigned' ? 'Unassigned' : '')}
                              size={16}
                              className='avatar' // Added class name for avatar text name
                              borderVariant={AVATAR_BORDER.THIN}
                            />
                          ) : filterName === 'project' ? (
                            <>
                              <ProjectIconAvatar
                                size={18}
                                type={projectDetails?.icon?.type || PROJECT_ICON_TYPE.DEFAULT}
                                reference={projectDetails?.icon?.reference || ''}
                                backgroundColor={ICON_COLORS[projectDetails?.icon?.color || 1]}
                                projectName={projectDetails?.name || ''}
                                borderRadius={'var(--border-radius-4)'}
                                iconSize={16}
                                isClickable={false}
                              />
                            </>
                          ) : filterName === 'customer' ? (
                            <Image
                              imageUrl={customer?.profileImage ? customer?.profileImage : 'empty_profile'}
                              alt='profile'
                              width={16}
                              height={16}
                              className={customer?.profileImage ? 'customer-avatar' : ''}
                            />
                          ) : (
                            <SVGIcon
                              name={renderStatusIconName(item as number)}
                              width={ICON_SIZE.MD}
                              height={ICON_SIZE.MD}
                              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                            />
                          )}
                        </IconDiv>
                      );
                    })}
                  <FilteredText>{filterDropdownContent?.label}</FilteredText>
                </Chip>
              }
              trigger='click'>
              {filterDropdown}
            </Dropdown>
            <Chip role='button' onClick={() => onRemoveFilter(filterName)}>
              <IconDiv className='chip-close-icon'>
                <SVGIcon
                  name='new-close-icon'
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-secondary)'
                />
              </IconDiv>
            </Chip>
          </TabSection>
        );
      })}
    </>
  );
};
export default observer(FilterChips);
